/**
 * usePWA — hook for PWA install prompt and update detection
 *
 * Usage:
 *   const { canInstall, install, updateAvailable, applyUpdate } = usePWA()
 */
import { useState, useEffect } from 'react'

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt]       = useState(null)
  const [canInstall, setCanInstall]               = useState(false)
  const [updateAvailable, setUpdateAvailable]     = useState(false)
  const [updateRegistration, setUpdateRegistration] = useState(null)
  const [isInstalled, setIsInstalled]             = useState(false)
  const [isIOS, setIsIOS]                         = useState(false)
  const [showIOSInstall, setShowIOSInstall]       = useState(false)

  useEffect(() => {
    // Detect if already running as installed PWA
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    setIsInstalled(isStandalone)

    // Detect iOS Safari. iOS does NOT support beforeinstallprompt, so there is no
    // programmatic install — the user must use Share → Add to Home Screen. We
    // detect iOS to show a manual hint instead. (Exclude in-app webviews where the
    // Share/A2HS flow isn't available; only real Safari can add to home screen.)
    const ua = window.navigator.userAgent || ''
    const iOSDevice = /iphone|ipad|ipod/i.test(ua) ||
      // iPadOS 13+ reports as Mac; detect via touch points
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios|chrome|android/i.test(ua)
    setIsIOS(iOSDevice)
    // Show the iOS hint only on real iOS Safari, and only when NOT already installed.
    setShowIOSInstall(iOSDevice && isSafari && !isStandalone)

    // Android/Chrome install prompt
    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setCanInstall(true)
    }

    // App installed (fired after install)
    const onAppInstalled = () => {
      setDeferredPrompt(null)
      setCanInstall(false)
      setIsInstalled(true)
    }

    // SW update available (fired from main.jsx)
    const onUpdateAvailable = (e) => {
      setUpdateAvailable(true)
      setUpdateRegistration(e.detail?.registration || null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onAppInstalled)
    window.addEventListener('pwa-update-available', onUpdateAvailable)

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onAppInstalled)
      window.removeEventListener('pwa-update-available', onUpdateAvailable)
    }
  }, [])

  /** Trigger the native Android/Chrome install prompt */
  async function install() {
    if (!deferredPrompt) return false
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    setCanInstall(false)
    return outcome === 'accepted'
  }

  /** Tell the waiting SW to take over, triggering a reload */
  function applyUpdate() {
    if (updateRegistration && updateRegistration.waiting) {
      updateRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }

  /** Dismiss the iOS install hint for this session. */
  function dismissIOSInstall() {
    setShowIOSInstall(false)
  }

  return { canInstall, install, updateAvailable, applyUpdate, isInstalled,
           isIOS, showIOSInstall, dismissIOSInstall }
}
